import { Module } from 'vuex';
import * as firebase from 'firebase/app';
import 'firebase/firestore';
import 'firebase/functions';
import router from '@/router';
import moment from 'moment';
import { serializer } from '../../api/shared/serializer';
import { MaintenanceType } from '@/models/MaintenanceType';
import {MaintenanceRequestStatus} from "@/enums/MaintenanceRequestStatus";
import {chunkArray} from "@/utils/chunkArray";
import { MaintenanceFormStatus } from '@/enums/MaintenanceFormStatus';
import { UserTypeEnum } from '@/enums/UserTypeEnum';
import { isTodayInMonthlyRepeatOn } from '@/utils/isTodayInMonthlyRepeatOn';
import { AuditLogsTier1 as Tier1, AuditLogsTier2 as Tier2, AuditLogsTier3 as Tier3 } from '@/enums/AuditLogs';
import { getRegionName } from '@/utils/functionsRegion';


const maintenanceModule: Module<any, any> = {
  namespaced: false,
  state: {
    maintenanceRequests: [],
    currentMaintenanceRequest: null,
    currentResidentMaintenanceRequests: [],
    maintenanceRequestAvgCompletion: [],
    maintenanceCategoryValues: [],
    maintenanceTypes: [],
    closedMaintenanceRequests: [],
    scheduledMaintenance: [],
    currentSchedMaint: {},
    currentSchedMaintList: [],
    allClosedMaintenanceRequests: [],
    maintenanceCategories: [],
    maintenanceTemplates: [],
    maintenanceTemplate: null,
    scheduledMaintenanceRequests: [],
    maintenanceForm: null,
    maintenanceFormsScheduledJob: [],
    maintenanceFormsRequestJob: []
  },

  mutations: {
    setMaintenanceRequests(state: any, payload: []) {
      state.maintenanceRequests = payload;
    },
    setMaintenanceRequestAvgCompletion(state: any, payload: []) {
      state.maintenanceRequestAvgCompletion = payload;
    },
    setCurrentMaintenanceRequest(state: any, payload: []) {
      state.currentMaintenanceRequest = payload;
    },
    setMaintenanceRequestsCurrentResident(state: any, payload: []) {
      state.currentResidentMaintenanceRequests = payload;
    },
    setMaintenanceCategoriyValues(state: any, payload: []) {
      state.maintenanceCategoryValues = payload;
    },
    setMaintenanceTypes(state: any, payload: MaintenanceType[]) {
      state.maintenanceTypes = payload;
    },
    setMaintenanceRequestsClosed(state: any, payload: MaintenanceType[]) {
      state.closedMaintenanceRequests = payload;
    },
    setScheduledMaintenance(state: any, payload: []) {
      state.scheduledMaintenance = payload;
    },
    setCurrentScheduledMaintenance(state: any, payload: []) {
      state.currentSchedMaint = payload;
    },
    setMaintenanceRequestsForSched(state: any, payload: []) {
      state.currentSchedMaintList = payload;
    },
    setAllMaintenanceRequestsClosed(state: any, payload: MaintenanceType[]) {
      state.allClosedMaintenanceRequests = payload;
    },
    setMaintenanceCategories(state, payload: []) {
      state.maintenanceCategories = payload;
    },
    setMaintenanceTemplates(state, payload: []) {
      state.maintenanceTemplates = payload;
    },
    setMaintenanceTemplate(state, payload: {}) {
      state.maintenanceTemplate = payload;
    },
    updateMaintenanceTemplateContent(state, payload: {}) {
      state.maintenanceTemplate.template = payload;
    },
    setScheduledMaintenanceRequests(state, payload: {}) {
      state.scheduledMaintenanceRequests = payload;
    },
    setMaintenanceForm(state, payload: {}) {
      state.maintenanceForm = payload;
    },
    updateMaintenanceFormContent(state, payload: {}) {
      state.maintenanceForm.template = payload;
    },
    setMaintenanceFormsByScheduledMaintenanceId(state, payload: {}) {
      state.maintenanceFormsScheduledJob = payload;
    },
    setMaintenanceFormsByMaintenanceRequestId(state, payload: {}) {
      state.maintenanceFormsRequestJob = payload;
    },
  },

  actions: {

    async getScheduledMaintenance({ commit }) {
      const facilityId = this.state.authModule.currentFacility;
      const maintenanceRequests = firebase
        .firestore()
        .collection('maintenanceScheduled')
        .where('facilityId', '==', facilityId);

      return await maintenanceRequests.get().then(async querySnapshot => {
        const maintReqs = [];
        const now = new Date();
        const docs = querySnapshot.docs;

        for (let i = 0; i < docs.length; i++) {
          const req = docs[i];
          const mntReg = req.data();
          mntReg.id = req.id;
          mntReg.maintainedBy = mntReg.maintainedBy || mntReg.vendor || mntReg.assignee;

          const isRequestsCompleted = await firebase
            .firestore()
            .collection("maintenanceRequests")
            .where("facilityId", "==", facilityId)
            .where("scheduledMaintenanceId", "==", mntReg.id)
            .orderBy("dateTime", "desc")
            .limit(5)
            .get()
            .then(async (querySnapshot) => {
              const maintenanceRequests = serializer.querySnapshotToDocuments(querySnapshot);
              if(maintenanceRequests && maintenanceRequests.length) {
                return maintenanceRequests.every((x: any) =>
                  (x.status === MaintenanceRequestStatus.Complete && x.completedDateTime) ||
                  (x.status === MaintenanceRequestStatus.Cancelled) ||
                  ([MaintenanceRequestStatus.InProgress, MaintenanceRequestStatus.New].includes(x.status) && moment().format('YYYY-MM-DD') <= moment(x.estimatedCompletionDate).format('YYYY-MM-DD'))
                );
              }

              return true;
            });

          mntReg.isRequestsCompleted = isRequestsCompleted;

          maintReqs.push(mntReg);
        }

        commit('setScheduledMaintenance', maintReqs);
      });
    },

    async getScheduledMaintenanceById({ commit }, id) {
      const snap = await firebase
        .firestore()
        .collection('maintenanceScheduled')
        .doc(id)
        .get();
      commit(
        'setCurrentScheduledMaintenance',
        serializer.documentSnapshotToDocument(snap)
      );
    },
    
    async getRequestsByScheduledMaintenanceId({ commit }, id) {
      const facilityId = this.state.authModule.currentFacility;
      const maintenanceRequests = firebase
        .firestore()
        .collection('maintenanceRequests')
        .where('facilityId', '==', facilityId)
        .where('scheduledMaintenanceId', '==' , id)
        .orderBy('dateTime');

      return await maintenanceRequests.get().then(querySnapshot => {
        const maintReqs: Array<any> = [];
        const now = new Date() as any;
        querySnapshot.docs.forEach(req => {
          const mntReg = req.data();
          const timestamp = new Date(mntReg.dateTime.seconds * 1000) as any;
          const minutesDiff = Math.floor((now - timestamp) / (1000 * 60));
          const completedDateTime = moment(mntReg.completedDateTime?.seconds * 1000) ?? null;
          const openDuration = moment.duration(completedDateTime.diff(moment(mntReg.dateTime.seconds * 1000).toDate())).asDays();

          mntReg.id = req.id;
          mntReg.dateTimeD = moment(mntReg.dateTime.seconds * 1000).toDate();
          mntReg.completionDateD = mntReg?.completedDateTime ? moment(mntReg.completedDateTime.seconds * 1000).toDate() : null;
          mntReg.requestAge = minutesDiff;
          mntReg.openDuration = Math.round(openDuration);
          maintReqs.push(mntReg);
        });
        commit('setMaintenanceRequestsForSched', maintReqs);
      });
    },

    async createMaintenanceForms(_, forms): Promise<any[]> {
      try {
        const formsArray = JSON.parse(JSON.stringify(forms));
        const createFormPromises = forms.map(async (form, index) => {
          const { formId, ...formData } = form;
          const docRef = await firebase
            .firestore()
            .collection('maintenanceForms')
            .add(formData);
          formsArray[index].formId = docRef.id;
          delete formsArray[index].facilityId;
          delete formsArray[index].result;
          delete formsArray[index].template;
          delete formsArray[index].templateName;
          delete formsArray[index].isMediaEnable;
          delete formsArray[index].media;
          return formsArray[index];
        });

        return await Promise.all(createFormPromises);
      } catch (error) {
        console.error('Error creating maintenance forms:', error);
        throw error;
      }
    },

    async createScheduledMaintenance({ dispatch }, { newSched, forms }) {
      newSched.createdDateTime = firebase.firestore.Timestamp.now();
      newSched.lastCompleted = null;

      await firebase
        .firestore()
        .collection('maintenanceScheduled')
        .add(newSched)
        .then((data) => {
          const isToday = newSched.startDate === moment(new Date()).format("YYYY-MM-DD");
          const isCustom = newSched.cadence === 'custom';
          const isDaily = newSched.recurringRepeatType === 'Day';
          const isWeekly = newSched.recurringRepeatType === 'Week';
          const isWeeklyOn = newSched.recurringRepetitionsDays.includes(moment(new Date()).format("ddd"));
          const isMonthly = newSched.recurringRepeatType === 'Month';
          const isTodayMatched = isTodayInMonthlyRepeatOn(newSched.monthlyRepeatOn);

          if(
            (isToday && !isCustom) ||
            (isToday && isCustom && isDaily) ||
            (isToday && isCustom && isWeekly && isWeeklyOn) ||
            (isToday && isCustom && isMonthly && isTodayMatched)
          ) {
            const facilityId = this.state.authModule.currentFacility;
            const newMaintenanceRequest: any = {};

            newMaintenanceRequest.status = (newSched.assigneeId && newSched.assigneeId?.length) ? MaintenanceRequestStatus.InProgress : MaintenanceRequestStatus.New;
            newMaintenanceRequest.dateTime = firebase.firestore.Timestamp.now();
            newMaintenanceRequest.scheduledMaintenanceId = data.id;
            newMaintenanceRequest.facilityId = facilityId;
            newMaintenanceRequest.locationId = newSched.locationId ?? null;
            newMaintenanceRequest.location = newSched.location ?? null;
            newMaintenanceRequest.wingId = newSched.wingId ?? null;
            newMaintenanceRequest.wing = newSched.wing ?? null;
            newMaintenanceRequest.description = newSched.description ?? null;
            newMaintenanceRequest.assigneeEmail = newSched.assigneeEmail ?? null;
            newMaintenanceRequest.assigneePhoneNumber = newSched.assigneePhoneNumber ?? null;
            newMaintenanceRequest.assigneeId = newSched.assigneeId ?? null;
            newMaintenanceRequest.assignee = newSched.assignee ?? null;
            newMaintenanceRequest.assignedDate = newSched.assigneeId ? firebase.firestore.Timestamp.now() : null;
            newMaintenanceRequest.name = newSched.title ?? "";
            newMaintenanceRequest.icon = newSched.icon ?? 'home-clock';
            newMaintenanceRequest.typeId = newSched.typeId ?? null;
            newMaintenanceRequest.department = newSched.department ?? null;
            newMaintenanceRequest.createdByStaffId = newSched.createdByStaffId ?? null;
            newMaintenanceRequest.createdByStaffName = newSched.createdByStaffName ?? null;
            newMaintenanceRequest.categoryId = newSched.categoryId;
            newMaintenanceRequest.category = newSched.category;
            newMaintenanceRequest.comment = newSched.comment ?? null;
            newMaintenanceRequest.priority = newSched.priority ?? null;
            newMaintenanceRequest.isSendEmailToVendor = newSched.isSendEmailToVendor ?? null;
            newMaintenanceRequest.estimatedCompletionDate = moment(new Date()).add(72, 'hours').format("YYYY-MM-DD");
            newMaintenanceRequest.isHazardous = newSched.isHazardous ?? null;
            newMaintenanceRequest.hazardousDetails = newSched.hazardousDetails ?? null;
            newMaintenanceRequest.risk = newSched.risk ?? null;
            newMaintenanceRequest.riskDetails = newSched.riskDetails ?? null;
            newMaintenanceRequest.maintenanceType = newSched.maintenanceType ?? null;
            newMaintenanceRequest.checklist = newSched.checklist ?? null;
            newMaintenanceRequest.media = newSched.media ?? null;
            newMaintenanceRequest.forms = forms ?? null;
            newMaintenanceRequest.formsIds = forms?.map(x => x?.formId) ?? null;

            dispatch("createMaintenanceRequest", newMaintenanceRequest);
          }

          dispatch("getMaintenanceRequests")
          dispatch("getScheduledMaintenance")
          return data;
        });        

    },

    async updateScheduledMaintenance({ dispatch }, { newSched, forms }) {
      const scheduledMaintenanceId = newSched.id;
      const scheduledMaintenance = Object.assign({}, newSched);
      delete scheduledMaintenance.id;

      await firebase
        .firestore()
        .collection('maintenanceScheduled')
        .doc(scheduledMaintenanceId)
        .update(scheduledMaintenance)
        .then(async (data) => {
          await firebase
            .firestore()
            .collection('maintenanceRequests')
            .where("facilityId", "==", this.state.authModule.currentFacility)
            .where("scheduledMaintenanceId", "==", scheduledMaintenanceId)
            .get()
            .then( async (querySnapshot) => {
              const maintenanceRequests = querySnapshot.docs.map(x => ({id: x.id, ref: x.ref, data: x.data()}));
              for(const maintenanceRequest of maintenanceRequests) {
                const updates: any = {
                  category: scheduledMaintenance.category,
                  categoryId: scheduledMaintenance.categoryId,
                  description: scheduledMaintenance.description ?? null,
                  name: scheduledMaintenance.title,
                  locationId: scheduledMaintenance.locationId ?? null,
                  location: scheduledMaintenance.location ?? null,
                  assignee: scheduledMaintenance.assignee ?? null,
                  assigneeId: scheduledMaintenance.assigneeId ?? null,
                  assigneeEmail: scheduledMaintenance.assigneeEmail ?? null,
                  assigneePhoneNumber: scheduledMaintenance.assigneePhoneNumber ?? null,
                  assignedDate: scheduledMaintenance.assigneeId ? firebase.firestore.Timestamp.now() : null,
                  wingId: scheduledMaintenance.wingId ?? null,
                  comment: scheduledMaintenance.comment ?? null,
                  priority: scheduledMaintenance.priority ?? null,
                  isSendEmailToVendor: scheduledMaintenance.isSendEmailToVendor ?? null,
                  icon: scheduledMaintenance.icon ?? 'home-clock',
                  typeId: scheduledMaintenance.typeId ?? null,
                  department: scheduledMaintenance.department ?? null,
                  isHazardous: scheduledMaintenance.isHazardous ?? null,
                  hazardousDetails: scheduledMaintenance.hazardousDetails ?? null,
                  risk: scheduledMaintenance.risk ?? null,
                  riskDetails: scheduledMaintenance.riskDetails ?? null,
                  maintenanceType: scheduledMaintenance.maintenanceType ?? null,
                  checklist: scheduledMaintenance.checklist ?? null,
                  media: scheduledMaintenance.media ?? null,
                  forms: [...(maintenanceRequest.data?.forms ?? []), ...(forms ?? [])],
                  formsIds: [...(maintenanceRequest.data?.formsIds ?? []), ...(forms?.map(x => x?.formId) ?? [])],
                }

                await maintenanceRequest.ref.update(updates);

                if(forms && forms?.length) {
                  const requestId: string = maintenanceRequest.id;
                  for(const formId of forms?.map(x => x?.formId)) {
                    await firebase
                      .firestore()
                      .collection("maintenanceForms")
                      .doc(formId)
                      .update({
                        requestId: requestId,
                        scheduledMaintenanceId: maintenanceRequest.data.scheduledMaintenanceId
                      })
                  }
                }
              }

              dispatch("getMaintenanceRequests")
              dispatch("getScheduledMaintenance")
            })
        })
    },

    async deleteScheduledMaintenance({ dispatch, state }, scheduledId) {
      const db = firebase.firestore();
      const facilityId = this.state.authModule.currentFacility;

      try {
        const maintenanceScheduledRef = await db.collection('maintenanceScheduled').doc(scheduledId);
        await maintenanceScheduledRef.update({
          deletedById: this.state.authModule.user.id,
          deletedByType: UserTypeEnum.Staff,
          typesOfRecordChanged: [`${Tier1.Maintenance}: ${Tier2.ScheduledJob}: ${Tier3.Remove}`]
        })
        await maintenanceScheduledRef.delete();

        const [formsSnapshot, requestsSnapshot] = await Promise.all([
          db.collection('maintenanceForms')
            .where("facilityId", "==", facilityId)
            .where("scheduledMaintenanceId", "==", scheduledId)
            .get(),

          db.collection('maintenanceRequests')
            .where("facilityId", "==", facilityId)
            .where("scheduledMaintenanceId", "==", scheduledId)
            .get()
        ]);

        const formDeletions = formsSnapshot.docs.map(doc => doc.ref.delete());
        const requestDeletions = requestsSnapshot.docs.map(doc => doc.ref.delete());

        await Promise.all([...formDeletions, ...requestDeletions]);

        await dispatch("getMaintenanceRequests");
        await dispatch("getScheduledMaintenance");
      } catch (error) {
        console.error("Error deleting scheduled maintenance:", error);
      }
    },
    
    async createMaintenanceRequest({ dispatch }, newMaintenanceRequest) {
      newMaintenanceRequest.dateTime =  firebase.firestore.Timestamp.now();
      const request = Object.assign({}, newMaintenanceRequest);
      delete request.id

      if(request?.assigneeId?.length){
        request.status = MaintenanceRequestStatus.InProgress
      }
      else{
        request.status = request.status || MaintenanceRequestStatus.New
      }

      // Generate ID only if not already set (e.g., by media upload)
      if(!newMaintenanceRequest.id){
        const snap = firebase.firestore().collection('maintenanceRequests').doc();
        newMaintenanceRequest.id = snap.id
      }

      const ref: any = firebase.firestore().collection('maintenanceRequests').doc(newMaintenanceRequest.id);
      await ref.set(request, { merge: true });

      if(request?.formsIds && request?.formsIds?.length) {
        for(const formId of request?.formsIds) {
          await firebase
            .firestore()
            .collection("maintenanceForms")
            .doc(formId)
            .update({
              requestId: ref.id,
              scheduledMaintenanceId: request.scheduledMaintenanceId
            })
        }
      }

      dispatch("getMaintenanceRequests")
    },

    async getMaintenanceRequests({ commit }) {
      const facilityId = this.state.authModule.currentFacility;
      const maintenanceRequests = firebase
        .firestore()
        .collection('maintenanceRequests')
        .where('facilityId', '==', facilityId)
        .where('status', 'in' , [MaintenanceRequestStatus.New, MaintenanceRequestStatus.InProgress])
        .orderBy('dateTime');
      return await maintenanceRequests.get().then(querySnapshot => {
        const maintReqs: Array<any> = [];
        const now = new Date() as any;
        querySnapshot.docs.forEach(req => {
          const mntReg = req.data();
          const timestamp = new Date(mntReg.dateTime.seconds * 1000) as any;
          const minutesDiff = Math.floor((now - timestamp) / (1000 * 60));
          mntReg.id = req.id;
          mntReg.requestAge = minutesDiff;
          maintReqs.push(mntReg);
        });
        commit('setMaintenanceRequests', maintReqs);
      });
    },

    async getMaintenanceRequest({ commit }, id) {
      const snap = await firebase
        .firestore()
        .collection('maintenanceRequests')
        .doc(id)
        .get();
      commit(
        'setCurrentMaintenanceRequest',
        serializer.documentSnapshotToDocument(snap)
      );
    },

    async editMaintenanceRequest({ dispatch }, request) {

      const currentReqSnap = await firebase
        .firestore()
        .collection("maintenanceRequests")
        .doc(request.id).get()

        if(request.updates?.assigneeId?.length && currentReqSnap.data()?.assigneeId != request.updates?.assigneeId){
          request.updates.status = MaintenanceRequestStatus.InProgress
        }

      await firebase
        .firestore()
        .collection("maintenanceRequests")
        .doc(request.id)
        .update({
          ...request.updates,
          updatedById: this.state.authModule.user.id,
          updatedByType: UserTypeEnum.Staff,
          typesOfRecordChanged: [`${Tier1.Maintenance}: ${Tier2.JobRequest}: ${Tier3.Amend}`]
        })
        .then(() => {
          dispatch("getMaintenanceRequest", request.id)
        })
    },

    async getClosedMaintenanceRequests({ commit }, { startDate, endDate }) {
      const maintenanceRequestsRef = firebase.firestore().collection('maintenanceRequests');

      const [closedRequestsSnapshot, cancelledRequestsSnapshot] = await Promise.all([
        maintenanceRequestsRef
          .where('facilityId', '==', this.state.authModule.currentFacility)
          .where('status', '==', MaintenanceRequestStatus.Complete)
          .where('completedDateTime', '>', startDate)
          .where('completedDateTime', '<', endDate)
          .get(),

        maintenanceRequestsRef
          .where('facilityId', '==', this.state.authModule.currentFacility)
          .where('status', '==', MaintenanceRequestStatus.Cancelled)
          .where('cancelledDateTime', '>', startDate)
          .where('cancelledDateTime', '<', endDate)
          .get()
      ]);

      const combinedRequests = [];

      closedRequestsSnapshot.docs.forEach(req => {
        const mntReg = req.data();
        const completedDateTime = moment(mntReg.completedDateTime.seconds * 1000);
        const openDuration = moment
          .duration(completedDateTime.diff(moment(mntReg.dateTime.seconds * 1000).toDate()))
          .asDays();
        mntReg.id = req.id;
        mntReg.openDuration = Math.round(openDuration);
        combinedRequests.push(mntReg);
      });

      cancelledRequestsSnapshot.docs.forEach(req => {
        const mntReg = req.data();
        const cancelledDateTime = moment(mntReg.cancelledDateTime.seconds * 1000);
        const openDuration = moment
          .duration(cancelledDateTime.diff(moment(mntReg.dateTime.seconds * 1000).toDate()))
          .asDays();
        mntReg.id = req.id;
        mntReg.openDuration = Math.round(openDuration);
        combinedRequests.push(mntReg);
      });

      commit('setMaintenanceRequestsClosed', combinedRequests);
    },

    async getAllClosedMaintenanceRequests({ commit }) {
      const maintenanceRequests = firebase
        .firestore()
        .collection('maintenanceRequests')
        .where('facilityId', '==', this.state.authModule.currentFacility)
        .where('status', '==', MaintenanceRequestStatus.Complete)

      await maintenanceRequests.get().then(querySnapshot => {
        const maintReqs: Array<any> = [];
        querySnapshot.docs.forEach(req => {
          const mntReg = req.data();
          if (mntReg.completedDateTime?.seconds) {
            const completedDateTime = moment(mntReg.completedDateTime.seconds * 1000);
            const openDuration = moment
              .duration(completedDateTime.diff(moment(mntReg.dateTime.seconds * 1000).toDate()))
              .asDays();
            mntReg.openDuration = Math.round(openDuration);
          }
          mntReg.id = req.id;
          maintReqs.push(mntReg);
        });
        commit('setAllMaintenanceRequestsClosed', maintReqs);
      })
    },

    async getMainRequestsByResident({ commit }, { residentId }) {
      const now = moment(new Date());
      const maintenanceRequests = firebase
        .firestore()
        .collection('maintenanceRequests')
        .where('status', '==', MaintenanceRequestStatus.New)
        .where('facilityId', '==', this.state.authModule.currentFacility)
        .where('residentId', '==', residentId)
        .orderBy('dateTime');
      await maintenanceRequests.get().then(querySnapshot => {
        const maintReqs: Array<any> = [];
        querySnapshot.docs.forEach(req => {
          const mntReg = req.data();
          const reqDateTime = moment
            .duration(now.diff(moment(mntReg.dateTime.seconds * 1000).toDate()))
            .asMinutes();
          mntReg.id = req.id;
          mntReg.requestAge = reqDateTime;
          maintReqs.push(mntReg);
        });
        commit('setMaintenanceRequestsCurrentResident', maintReqs);
      });
    },

    async getMaintenanceRequestAvgCompletion({ commit }) {
      const maintenanceRequests = firebase
        .firestore()
        .collection('maintenanceRequests')
        .where('facilityId', '==', this.state.authModule.currentFacility)
        .where('status', '==', MaintenanceRequestStatus.Complete);
      await maintenanceRequests.get().then(querySnapshot => {
        const maintReqs: Array<any> = [];
        querySnapshot.docs.forEach(req => {
          const mntReg = req.data();
          const now = moment(mntReg.completedDateTime.seconds * 1000);
          const completionTime = moment
            .duration(now.diff(moment(mntReg.dateTime.seconds * 1000).toDate()))
            .asMinutes();
          mntReg.id = req.id;
          mntReg.completionTime = completionTime;
          maintReqs.push(mntReg);
        });
        commit('setMaintenanceRequestAvgCompletion', maintReqs);
      });
    },

    async getMaintenanceCategoryValues({ commit }) {
      const maintenanceRequests = firebase
        .firestore()
        .collection('maintenanceRequests')
        .where('facilityId', '==', this.state.authModule.currentFacility);

      await maintenanceRequests.get().then(querySnapshot => {
        const maintReqs: Array<any> = [];
        let lightbulb = 0;
        let plumbing = 0;
        let cleaning = 0;
        let tvtablet = 0;
        let bathroom = 0;
        let batteries = 0;
        querySnapshot.docs.forEach(req => {
          const mntReg = req.data();
          if (mntReg.category == 'lightbulb') {
            lightbulb++;
          } else if (mntReg.category == 'plumbing') {
            plumbing++;
          } else if (mntReg.category == 'cleaning') {
            cleaning++;
          } else if (mntReg.category == 'TVTablet') {
            tvtablet++;
          } else if (mntReg.category == 'bathroom') {
            bathroom++;
          } else if (mntReg.category == 'batteries') {
            batteries++;
          }
        });
        maintReqs.push(
          plumbing,
          cleaning,
          lightbulb,
          tvtablet,
          bathroom,
          batteries
        );
        commit('setMaintenanceCategoriyValues', maintReqs);
      });
    },

    async completeMaintenanceRequest({ commit }, { id, additionalResponse }) {
      const maintenanceRequests = firebase
        .firestore()
        .collection('maintenanceRequests')
        .doc(id);
      const completedDateTime = firebase.firestore.Timestamp.now();
      await maintenanceRequests.update({
        status: 'complete',
        additionalResponse: additionalResponse,
        completedDateTime: completedDateTime,
        updatedById: this.state.authModule.user.id,
        updatedByType: UserTypeEnum.Staff,
        typesOfRecordChanged: [`${Tier1.Maintenance}: ${Tier2.JobRequest}: ${Tier3.Amend}`]
      });

      router.push('/maintenanceRequests');
    },

    async assignMaintenanceRequest({ commit }, { id, assignee, assigneeId, assigneeEmail, assigneePhoneNumber, estimatedCompletionDate, priority, isSendEmailToVendor }) {
      const maintenanceRequests = firebase
        .firestore()
        .collection('maintenanceRequests')
        .doc(id);
      const today = firebase.firestore.Timestamp.now();
      await maintenanceRequests.update({
        status: MaintenanceRequestStatus.InProgress,
        assignedDate: today,
        assignee: assignee,
        assigneeId: assigneeId,
        assigneeEmail: assigneeEmail,
        assigneePhoneNumber: assigneePhoneNumber,
        estimatedCompletionDate: estimatedCompletionDate,
        priority: priority,
        isSendEmailToVendor: isSendEmailToVendor,
        updatedById: this.state.authModule.user.id,
        updatedByType: UserTypeEnum.Staff,
        typesOfRecordChanged: [`${Tier1.Maintenance}: ${Tier2.JobRequest}: ${Tier3.Amend}`]
      });

      router.push(`/maintenanceRequests?tab=2`);
    },

    async getMaintenanceTypes({ commit }) {
      await firebase
        .firestore()
        .collection('maintenanceRequestTypes')
        .where('facilityId', '==', this.state.authModule.currentFacility)
        .get()
        .then(querySnapshots => {
          const maintenanceTypes: MaintenanceType[] = serializer.querySnapshotToDocuments(
            querySnapshots
          );
          commit('setMaintenanceTypes', maintenanceTypes);
        });
    },

    async createMaintenanceType(
      { dispatch },
      maintenanceType: MaintenanceType
    ) {
      try {
         maintenanceType.createdById = this.state.authModule.user.id
         maintenanceType.createdByType = UserTypeEnum.Staff
         maintenanceType.typesOfRecordChanged = [`${Tier1.StaffHubSettings}: ${Tier2.StaffSettings}: ${Tier3.Maintenance}`]

        await firebase
          .firestore()
          .collection('maintenanceRequestTypes')
          .add(maintenanceType)
          .then(() => {
            dispatch('getMaintenanceTypes');
          });
      } catch (error) {
        return error;
      }
    },

    async updateMaintenanceType(
      { dispatch },
      maintenanceType: MaintenanceType
    ) {
      try {
        await firebase
          .firestore()
          .collection('maintenanceRequestTypes')
          .doc(maintenanceType.id)
          .update({
            name: maintenanceType.name,
            category: maintenanceType.category,
            categoryId: maintenanceType.categoryId,
            icon: maintenanceType.icon,
            department: maintenanceType.department,
            updatedById: this.state.authModule.user.id,
            updatedByType: UserTypeEnum.Staff,
            typesOfRecordChanged: [`${Tier1.StaffHubSettings}: ${Tier2.StaffSettings}: ${Tier3.Maintenance}`]
          })
          .then(() => {
            dispatch('getMaintenanceTypes');
            dispatch('updateMaintenanceRequestsByType', maintenanceType);
          });
      } catch (error) {
        return error;
      }
    },

    async updateMaintenanceRequestsByType(_, maintenanceType: MaintenanceType) {
      try {
        await firebase
          .firestore()
          .collection('maintenanceRequests')
          .where('facilityId', '==', this.state.authModule.currentFacility)
          .where('typeId', '==', maintenanceType.id)
          .get()
          .then(querySnapshot => {
            if (querySnapshot.docs && querySnapshot.docs.length) {
              querySnapshot.docs.forEach(doc => {
                doc.ref.update({
                  name: maintenanceType.name,
                  category: maintenanceType.category,
                  icon: maintenanceType.icon,
                  department: maintenanceType.department,
                });
              });
            }
          });
      } catch (error) {
        return error;
      }
    },

    async updateMaintenanceRequestsByResident(_, resident) {
      try {
        await firebase
          .firestore()
          .collection('maintenanceRequests')
          .where('facilityId', '==', this.state.authModule.currentFacility)
          .where('residentId', '==', resident.id)
          .where('status', '==', MaintenanceRequestStatus.New)
          .get()
          .then(querySnapshot => {
            if (querySnapshot.docs && querySnapshot.docs.length) {
              const updates: any = {};
              const roomName = resident?.roomName || '';
              const address = resident?.address || '';

              if (roomName || address) {
                updates.location = `${roomName} ${address}`;
              }

              if (resident?.hasOwnProperty('name') && resident?.name) {
                updates.residentName = resident.name;
              }

              if (resident?.hasOwnProperty('phone') && resident?.phone) {
                updates.phone = resident?.phone;
              }

              if (Object.keys(updates).length) {
                querySnapshot.docs.forEach(doc => {
                  doc.ref.update(updates);
                });
              }
            }
          });
      } catch (error) {
        return error;
      }
    },

    async deleteMaintenanceType(
      { dispatch },
      maintenanceType: MaintenanceType
    ) {
      try {

        const ref = firebase
          .firestore()
          .collection('maintenanceRequestTypes')
          .doc(maintenanceType.id)

          await ref.update({
             deletedById: this.state.authModule.user.id,
             deletedByType: UserTypeEnum.Staff,
             typesOfRecordChanged: [`${Tier1.StaffHubSettings}: ${Tier2.StaffSettings}: ${Tier3.Maintenance}`]
          })

        await ref.delete().then(() => {
            dispatch('getMaintenanceTypes');
          });
      } catch (error) {
        return error;
      }
    },

    async getMaintenanceCategories({ commit }) {
      try {
        await firebase
          .firestore()
          .collection('maintenanceCategories')
          .where("facilityId", "==", this.state.authModule.currentFacility)
          .get()
          .then((querySnapshot) => {
            const maintenanceCategories = serializer.querySnapshotToDocuments(querySnapshot);
            commit('setMaintenanceCategories', maintenanceCategories)
          })
      } catch (err) {
        return err;
      }
    },

    async createMaintenanceCategory({ dispatch }, maintenanceCategory) {
      try {
        maintenanceCategory.createdById = this.state.authModule.user.id,
        maintenanceCategory.createdByType = UserTypeEnum.Staff,
        maintenanceCategory.typesOfRecordChanged = [`${Tier1.StaffHubSettings}: ${Tier2.StaffSettings}: ${Tier3.Maintenance}`]
        await firebase
          .firestore()
          .collection('maintenanceCategories')
          .add(maintenanceCategory)
          .then(() => {
            dispatch("getMaintenanceCategories")
          })
      } catch (err) {
        return err;
      }
    },

    async updateMaintenanceCategory({ dispatch }, maintenanceCategory) {
      try {
        await firebase
          .firestore()
          .collection('maintenanceCategories')
          .doc(maintenanceCategory.id)
          .update({
            name: maintenanceCategory.name,
            updatedById: this.state.authModule.user.id,
            updatedByType: UserTypeEnum.Staff,
            typesOfRecordChanged: [`${Tier1.StaffHubSettings}: ${Tier2.StaffSettings}: ${Tier3.Maintenance}`]
          })
          .then(() => {
            dispatch("getMaintenanceCategories")
          })
      } catch (err) {
        return err;
      }
    },

    async updateMaintenanceCategoryForSelectedCollection(_, data) {
      try {
        await firebase
          .firestore()
          .collection(data.collection)
          .where("facilityId", "==", this.state.authModule.currentFacility)
          .where("categoryId", "==", data.maintenanceCategory.id)
          .get()
          .then(async (querySnapshot) => {
            const vendors = querySnapshot.docs.map(x => ({ id: x.id, ref: x.ref, data: x.data() }))

            for(const vendor of vendors) {
              await vendor.ref.update({
                category: data.maintenanceCategory.name,
                updatedById: this.state.authModule.user.id,
                updatedByType: UserTypeEnum.Staff,
                typesOfRecordChanged: [`${Tier1.StaffHubSettings}: ${Tier2.StaffSettings}: ${Tier3.Maintenance}`]
              })
            }
          })
      } catch (err) {
        return err;
      }
    },

    async deleteMaintenanceCategory({ dispatch }, maintenanceCategory) {
      try {
        const ref = firebase.firestore().collection('maintenanceCategories').doc(maintenanceCategory.id)

        await ref.update({
            deletedById: this.state.authModule.user.id,
            deletedByType: UserTypeEnum.Staff,
            typesOfRecordChanged: [`${Tier1.StaffHubSettings}: ${Tier2.StaffSettings}: ${Tier3.Maintenance}`]
        })

        await ref.delete().then(() => {
          dispatch("getMaintenanceCategories")
        })
      } catch (err) {
        return err;
      }
    },

    async getMaintenanceReport({ dispatch }, { startDate, endDate }) {
      const report = {
        requests: null,
        assignedRequests: null
      }

      report.requests = await firebase
        .firestore()
        .collection("maintenanceRequests")
        .where("facilityId", "==", this.state.authModule.currentFacility)
        .where("dateTime", ">", startDate)
        .where("dateTime", "<", endDate)
        .get()
        .then((querySnapshot) => {
          const maintenanceRequests: Array<any> = [];

          querySnapshot.docs.forEach((x) => {
            const request = x.data();
            request.id = x.id;

            if(request.status === MaintenanceRequestStatus.New) {
              request.color = "primary--text";
            } else if(request.status === MaintenanceRequestStatus.InProgress) {
              request.color = "orange--text";
            } else if(request.status === MaintenanceRequestStatus.Complete) {
              request.color = "green--text";
            } else if(request.status === MaintenanceRequestStatus.Cancelled) {
              request.color = "red--text";
            }

            maintenanceRequests.push(request);
          });

          return maintenanceRequests;
        })

      const totalRequests = report.requests.filter(x => x?.assignee && x?.assignedDate).length;

      const assignedRequests = Object.entries(
        report.requests
          .filter(x => x?.assignee && x?.assignedDate)
          .reduce((acc, item) => {
            const assignee = item?.assigneeEmail ?? item?.assignee;
            acc[assignee] = (acc[assignee] || 0) + 1;
            return acc;
          }, {})
      ).map(([email, count]) => {
        const numericCount = Number(count);
        const numericTotalRequests = Number(totalRequests);

        if (isNaN(numericCount) || isNaN(numericTotalRequests)) {
          throw new Error("Invalid data: count or totalRequests is NaN");
        }

        const percent = numericTotalRequests === 0 ? "0.00" : ((numericCount / numericTotalRequests) * 100).toFixed(2);

        return {
          email: email,
          count: numericCount,
          percent: percent,
          name: ""
        };
      });

      for(const assignedRequest of assignedRequests) {
        let name = await firebase
         .firestore()
         .collection("vendors")
         .where("facilityId", "==", this.state.authModule.currentFacility)
         .where("email", "==", assignedRequest.email)
         .get()
         .then(async (querySnapshot) => {
           const maintenanceTechnicians: any = serializer.querySnapshotToDocuments(querySnapshot);
           return maintenanceTechnicians[0]?.name;
         })

        if(!name) {
          name = await firebase
           .firestore()
           .collection("users")
           .where("orgId", "==", this.state.authModule.currentFacilityData.orgId)
           .where("email", "==", assignedRequest.email)
           .get()
           .then(async (querySnapshot) => {
             const users: any = serializer.querySnapshotToDocuments(querySnapshot);
             return users[0]?.name;
           })
         }

        assignedRequest.name = name || "User Removed";
      }

      report.assignedRequests = assignedRequests;

      return report;
    },

    async addMaintenanceTemplate({ dispatch }, template) {
      try {
        return await firebase
          .firestore()
          .collection('maintenanceTemplates')
          .add({
            ...template,
            createdById: this.state.authModule.user.id,
            createdByType: UserTypeEnum.Staff,
            typesOfRecordChanged: [`${Tier1.Maintenance}: ${Tier2.FormTemplates}: ${Tier3.Add}`]
          })
          .then((querySnapshot) => {
            dispatch("getMaintenanceTemplates")
            return querySnapshot;
          })
      } catch (err) {
        return err;
      }
    },

    async getMaintenanceTemplates({ commit }) {
      try {
        await firebase
          .firestore()
          .collection('maintenanceTemplates')
          .where("facilityId", "==", this.state.authModule.currentFacility)
          .get()
          .then((querySnapshot) => {
            const templates: any = serializer.querySnapshotToDocuments(querySnapshot);
            commit("setMaintenanceTemplates", templates);
          })
      } catch (err) {
        return err;
      }
    },

    async getMaintenanceTemplate({ commit }, id: string) {
      try {
        await firebase
          .firestore()
          .collection('maintenanceTemplates')
          .doc(id)
          .get()
          .then((querySnapshot) => {
            const template: any = serializer.documentSnapshotToDocument(querySnapshot);
            commit("setMaintenanceTemplate", template);
          })
      } catch (err) {
        return err;
      }
    },

    async saveMaintenanceTemplate({ commit, dispatch }, { id, template } ) {
      try {
        await firebase
          .firestore()
          .collection('maintenanceTemplates')
          .doc(id)
          .update({
            ...template,
            updatedById: this.state.authModule.user.id,
            updatedByType: UserTypeEnum.Staff,
            typesOfRecordChanged: [`${Tier1.Maintenance}: ${Tier2.FormTemplates}: ${Tier3.Amend}`]
          })
         await dispatch("getMaintenanceTemplate", id);
      } catch (err) {
        return err;
      }
    },

    async deleteMaintenanceTemplate({ dispatch }, id: string) {
      try {
        const templateRef = await firebase.firestore().collection('maintenanceTemplates').doc(id);

        await templateRef.update({
          deletedById: this.state.authModule.user.id,
          deletedByType: UserTypeEnum.Staff,
          typesOfRecordChanged: [`${Tier1.Maintenance}: ${Tier2.FormTemplates}: ${Tier3.Remove}`]
        });

        await templateRef.delete().then(() => {
          dispatch("getMaintenanceTemplates");
        });
      } catch (err) {
        return err;
      }
    },

    async getScheduledMaintenanceRequests({ commit }) {
      try {
        const scheduledMaintenanceIds = this.state.maintenanceModule.scheduledMaintenance.map(x => x.id);
        const chunks = chunkArray(scheduledMaintenanceIds, 10);
        const facilityId = this.state.authModule.currentFacility;
        const promises = chunks.map(chunk => {
          return firebase
            .firestore()
            .collection('maintenanceRequests')
            .where('facilityId', '==', facilityId)
            .where('scheduledMaintenanceId', 'in', chunk)
            .get();
        });

        const snapshots = await Promise.all(promises);
        const allDocs = snapshots.flatMap((snapshot: any) => snapshot.docs);
        const requests = allDocs.map(doc => {
          return {
            ...doc.data(),
            id: doc.id
          }
        });

        commit('setScheduledMaintenanceRequests', requests);
      } catch (err) {
        console.error("ERROR ", err)
        return err;
      }
    },

    async getMaintenanceForm({ commit }, formId: string) {
      try {
        await firebase
          .firestore()
          .collection('maintenanceForms')
          .doc(formId)
          .get()
          .then((querySnapshot) => {
            const form: any = serializer.documentSnapshotToDocument(querySnapshot);
            commit("setMaintenanceForm", form);
          })
      } catch (err) {
        return err;
      }
    },

    async getMaintenanceFormsByScheduledMaintenanceId({ commit }, id) {
      try {
        await firebase
          .firestore()
          .collection('maintenanceForms')
          .where("facilityId", "==", this.state.authModule.currentFacility)
          .where("scheduledMaintenanceId", "==", id)
          .get()
          .then((querySnapshot) => {
            const forms: any = serializer.querySnapshotToDocuments(querySnapshot);
            commit('setMaintenanceFormsByScheduledMaintenanceId', forms);
          })
      } catch (err) {
        return err;
      }
    },

    async getMaintenanceFormsByMaintenanceRequestId({ commit }, id) {
      try {
        await firebase
          .firestore()
          .collection('maintenanceForms')
          .where("facilityId", "==", this.state.authModule.currentFacility)
          .where("requestId", "==", id)
          .get()
          .then((querySnapshot) => {
            const forms: any = serializer.querySnapshotToDocuments(querySnapshot);
            commit('setMaintenanceFormsByMaintenanceRequestId', forms);
          })
      } catch (err) {
        return err;
      }
    },


    async saveMaintenanceForm({ commit }, { id, template } ) {
      try {
        await firebase
          .firestore()
          .collection('maintenanceForms')
          .doc(id)
          .update(template)
      } catch (err) {
        return err;
      }
    },

    async deleteMaintenanceForm({ commit }, { id } ) {
      try {
        await firebase
          .firestore()
          .collection('maintenanceForms')
          .doc(id)
          .delete();
      } catch (err) {
        return err;
      }
    },

    async deleteMaintenanceFormsByScheduledMaintenance({ commit }, { maintenanceScheduleId, form }) {
      try {
        await firebase
          .firestore()
          .collection('maintenanceForms')
          .where("facilityId", "==", this.state.authModule.currentFacility)
          .where("scheduledMaintenanceId", "==", maintenanceScheduleId)
          .where("locationId", "==", form.locationId)
          .where("templateId", "==", form.templateId)
          .where("name", "==", form.name)
          .where("status", "==", MaintenanceFormStatus.InProgress)
          .get()
          .then((querySnapshot) => {
            console.log("querySnapshot ", querySnapshot);
            for (const form of querySnapshot.docs) {
              form.ref.delete();
            }
          })
      } catch (err) {
        return err;
      }
    },

    async deleteMaintenanceFormFromRequestById({ commit }, { requestId, formId } ) {
      try {
        await firebase
          .firestore()
          .collection('maintenanceRequests')
          .doc(requestId)
          .get()
          .then((querySnapshot) => {
            const request: any = serializer.documentSnapshotToDocument(querySnapshot);
            const newForms = request.forms.filter(x => x.formId !== formId);
            const newFormsIds = newForms.map(x => x.formId);

            querySnapshot.ref.update({
              forms: newForms,
              formsIds: newFormsIds
            })
          })
      } catch (err) {
        return err;
      }
    },

    async deleteMaintenanceFormsFromRequestsByScheduledId(_, { maintenanceScheduleId, form }) {
      try {
        await firebase
          .firestore()
          .collection('maintenanceRequests')
          .where("facilityId", "==", this.state.authModule.currentFacility)
          .where("status", "in", [MaintenanceRequestStatus.InProgress, MaintenanceRequestStatus.New])
          .where("scheduledMaintenanceId", "==", maintenanceScheduleId)
          .get()
          .then((querySnapshot) => {
            const requests: any = querySnapshot.docs.map(x => ({id: x.id, ref: x.ref, data: x.data()}));
            for(const request of requests) {
              const requestForm = request.data.forms.find(x => x.templateId === form.templateId && x.locationId === form.locationId && x.name === form.name && x.status === MaintenanceFormStatus.InProgress);

              if(requestForm) {
                const newForms = request.data.forms.filter(x => x.formId !== requestForm.formId);
                const newFormsIds = newForms.map(x => x.formId);

                request.ref.update({
                  forms: newForms,
                  formsIds: newFormsIds
                })
              }
            }
          });
      } catch (err) {
        return err;
      }
    },

    async deleteMaintenanceFormFromMaintenanceSchedule({ commit }, { maintenanceScheduleId, form } ) {
      try {
        await firebase
          .firestore()
          .collection('maintenanceScheduled')
          .doc(maintenanceScheduleId)
          .update({
            forms: firebase.firestore.FieldValue.arrayRemove(form)
          })
      } catch (err) {
        return err;
      }
    },

    async batchCancelMaintenanceRequests({ dispatch }, { requestIds, cancelReason }) {
      const facilityId = this.state.authModule.currentFacility;
      const staffName = this.state.authModule.user?.name || 'Unknown';
      const staffAccessLevel = this.state.authModule.user?.accessLevel || 'Unknown';
      
      const batchCancelFunction = firebase.app().functions(getRegionName()).httpsCallable('maintenance-batchCancelMaintenanceRequests');
      
      const result = await batchCancelFunction({
        requestIds,
        facilityId,
        cancelReason,
        staffName,
        staffAccessLevel
      });
      
      await dispatch('getMaintenanceRequests');
      
      return result.data;
    },

    async batchUpdateMaintenanceRequests({ dispatch }, { requestIds, updates }) {
      const facilityId = this.state.authModule.currentFacility;
      const staffName = this.state.authModule.user?.name || 'Unknown';
      const staffAccessLevel = this.state.authModule.user?.accessLevel || 'Unknown';
      
      const batchUpdateFunction = firebase.app().functions(getRegionName()).httpsCallable('maintenance-batchUpdateMaintenanceRequests');
      
      const result = await batchUpdateFunction({
        requestIds,
        facilityId,
        updates,
        staffName,
        staffAccessLevel
      });
      
      await dispatch('getMaintenanceRequests');
      
      return result.data;
    },

    async batchAssignMaintenanceRequests({ dispatch }, { requestIds, assignee, sendEmail }) {
      const facilityId = this.state.authModule.currentFacility;
      const staffName = this.state.authModule.user?.name || 'Unknown';
      const staffAccessLevel = this.state.authModule.user?.accessLevel || 'Unknown';
      
      const batchAssignFunction = firebase.app().functions(getRegionName()).httpsCallable('maintenance-batchAssignMaintenanceRequests');
      
      const result = await batchAssignFunction({
        requestIds,
        facilityId,
        assignee,
        sendEmail,
        staffName,
        staffAccessLevel
      });
      
      await dispatch('getMaintenanceRequests');
      
      return result.data;
    },

  },
  
  getters: {
    maintenanceCategories(state) {
      return [{ id: "All", name: "All" }, ...state.maintenanceCategories];
    }
  }
};

export default maintenanceModule;
